import { Button, DropdownMenu } from "@radix-ui/themes";
import { Toolbar } from "radix-ui";
import type { CSSProperties } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ImportExportLyric } from "$/modules/project/modals/ImportExportLyric";
import { formatKeyBindings } from "$/utils/keybindings";
import { useTopMenuActions } from "../useTopMenuActions";

type FileMenuProps = {
	variant: "toolbar" | "submenu";
	buttonStyle?: CSSProperties;
};

const FileMenuItems = () => {
	const { t } = useTranslation();
	const menu = useTopMenuActions();

	return (
		<>
			<DropdownMenu.Item
				onSelect={menu.onNewFile}
				shortcut={formatKeyBindings(menu.newFileKey)}
			>
				<Trans i18nKey="topBar.menu.newLyric">新しい歌詞</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Item
				onSelect={menu.onOpenFile}
				shortcut={formatKeyBindings(menu.openFileKey)}
			>
				<Trans i18nKey="topBar.menu.openLyric">歌詞を開く</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Item
				onSelect={menu.onSaveFile}
				shortcut={formatKeyBindings(menu.saveFileKey)}
			>
				<Trans i18nKey="topBar.menu.saveLyricOverwrite">歌詞を上書き保存</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Item onSelect={menu.onSaveFileAs}>
				<Trans i18nKey="topBar.menu.saveLyricAs">名前をつけて歌詞を保存</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Separator />
			<DropdownMenu.Item onSelect={menu.onLoadAudio}>
				<Trans i18nKey="topBar.menu.loadAudio">音声を読み込む</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Item onSelect={menu.onLoadAudioFromSpotify}>
				<Trans i18nKey="topBar.menu.loadAudioFromSpotify">
					Spotify から取得
				</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Separator />
			<DropdownMenu.Sub>
				<DropdownMenu.SubTrigger>
					<Trans i18nKey="topBar.menu.fetchLyrics">歌詞を取得</Trans>
				</DropdownMenu.SubTrigger>
				<DropdownMenu.SubContent>
					<DropdownMenu.Item onSelect={menu.onOpenFromAppleMusic}>
						<Trans i18nKey="topBar.menu.openFromAppleMusic">
							Apple Music から取得
						</Trans>
					</DropdownMenu.Item>
					<DropdownMenu.Item onSelect={menu.onOpenFromUtanet}>
						<Trans i18nKey="topBar.menu.openFromUtanet">Uta-Net から取得</Trans>
					</DropdownMenu.Item>
					<DropdownMenu.Item onSelect={menu.onOpenFromJoysound}>
						<Trans i18nKey="topBar.menu.openFromJoysound">
							JOYSOUND から取得
						</Trans>
					</DropdownMenu.Item>
				</DropdownMenu.SubContent>
			</DropdownMenu.Sub>
			<DropdownMenu.Item onSelect={menu.onUploadToTtmlDb}>
				<Trans i18nKey="topBar.menu.uploadToTtmlDb">
					データベースにアップロード…
				</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Item onSelect={menu.onDeliverToExpress}>
				<Trans i18nKey="topBar.menu.deliverToExpress">特急に納品…</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Separator />
			<DropdownMenu.Item onSelect={menu.onOpenHistoryRestore}>
				{t("topBar.menu.restoreFromHistory", "履歴から復元…")}
			</DropdownMenu.Item>
			<DropdownMenu.Separator />
			<DropdownMenu.Item onSelect={menu.onOpenFileFromClipboard}>
				<Trans i18nKey="topBar.menu.openFromClipboard">
					クリップボードから TTML を開く
				</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Item onSelect={menu.onSaveFileToClipboard}>
				<Trans i18nKey="topBar.menu.saveLyricToClipboard">
					TTML をクリップボードに保存
				</Trans>
			</DropdownMenu.Item>
			<DropdownMenu.Separator />
			<ImportExportLyric />
			<DropdownMenu.Separator />
			<DropdownMenu.Item color="red" onSelect={menu.onLogout}>
				<Trans i18nKey="topBar.menu.logout">ログアウト</Trans>
			</DropdownMenu.Item>
		</>
	);
};

export const FileMenu = (props: FileMenuProps) => {
	if (props.variant === "submenu") {
		return (
			<DropdownMenu.Sub>
				<DropdownMenu.SubTrigger>
					<Trans i18nKey="topBar.menu.file">ファイル</Trans>
				</DropdownMenu.SubTrigger>
				<DropdownMenu.SubContent>
					<FileMenuItems />
				</DropdownMenu.SubContent>
			</DropdownMenu.Sub>
		);
	}

	return (
		<DropdownMenu.Root>
			<Toolbar.Button asChild>
				<DropdownMenu.Trigger>
					<Button variant="soft" style={props.buttonStyle}>
						<Trans i18nKey="topBar.menu.file">ファイル</Trans>
					</Button>
				</DropdownMenu.Trigger>
			</Toolbar.Button>
			<DropdownMenu.Content>
				<FileMenuItems />
			</DropdownMenu.Content>
		</DropdownMenu.Root>
	);
};
